import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, CalendarClock, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RescheduleModal({ appointment, onClose, onDone }) {
  const [date, setDate] = useState(appointment.date);
  const [slots, setSlots] = useState([]);
  const [meta, setMeta] = useState(null);
  const [slot, setSlot] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!date) return;
    axios.get(`${API}/doctors/${appointment.doctor_id}/slots`, { params: { date }, withCredentials: true })
      .then((r) => { setSlots(r.data.slots || []); setMeta(r.data); setSlot(""); })
      .catch(() => setSlots([]));
  }, [date, appointment.doctor_id]);

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const r = await axios.put(`${API}/patient/appointments/${appointment.id}/reschedule`, { date, time_slot: slot }, { withCredentials: true });
      onDone(r.data.booking);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not reschedule");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4" data-testid="reschedule-modal">
      <div className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl">
        <div className="bg-[#0D3B2E] text-white p-5 rounded-t-3xl flex justify-between items-start">
          <div>
            <h3 className="font-serif-luxury text-lg font-bold flex items-center gap-2"><CalendarClock className="w-5 h-5 text-amber-300" /> Reschedule appointment</h3>
            <p className="text-xs text-emerald-200">{appointment.doctor_name} · currently {appointment.date}, {appointment.time_slot}</p>
          </div>
          <button onClick={onClose} data-testid="reschedule-close-btn" className="text-gray-300 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {error && <p data-testid="reschedule-error" className="p-2.5 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-xl">{error}</p>}
          <input type="date" min={new Date().toISOString().split("T")[0]} value={date} onChange={(e) => setDate(e.target.value)} data-testid="reschedule-date-input" className="w-full px-3 py-2.5 bg-[#FBF9F4] border border-gray-200 rounded-xl text-xs" />
          {meta && !meta.is_working_day ? (
            <p className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="reschedule-off-day">{meta.off_reason || "No OPD"} on {date}. {meta.next_available_date && <button type="button" onClick={() => setDate(meta.next_available_date)} className="underline font-semibold">Next OPD day {meta.next_available_date}</button>}</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="reschedule-slot-grid">
              {slots.map((s) => {
                const isCurrent = date === appointment.date && s.slot === appointment.time_slot;
                const disabled = !s.is_available || isCurrent;
                return (
                  <button key={s.slot} type="button" disabled={disabled} onClick={() => setSlot(s.slot)} data-testid={`reschedule-slot-${s.slot.replace(/[\s:]/g, "-")}`}
                    className={`py-2 text-xs rounded-xl font-medium border ${disabled ? "bg-gray-100 text-gray-400 line-through border-gray-200" : slot === s.slot ? "bg-[#0D3B2E] text-white border-[#0D3B2E]" : "bg-[#FBF9F4] text-gray-800 border-gray-200 hover:border-[#3B8271]"}`}>
                    {s.slot}{isCurrent && <span className="block text-[9px] no-underline">current</span>}
                  </button>
                );
              })}
            </div>
          )}
          <button type="button" disabled={!slot || busy} onClick={submit} data-testid="reschedule-confirm-btn" className="w-full py-3 bg-[#0D3B2E] disabled:opacity-40 text-white text-xs font-semibold rounded-xl flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4 text-amber-300" />} Move to {slot ? `${date} · ${slot}` : "selected slot"}
          </button>
        </div>
      </div>
    </div>
  );
}
